import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, PlusSquare, Bell, MessageSquare, LogOut, User, ChevronDown, Wallet } from 'lucide-react';
import axios from 'axios';

const Header = ({ isLoggedIn, user, logout }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [chatUnread, setChatUnread] = useState(0);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Fetch unread notification count whenever user logs in or navigates away from /notifications
    useEffect(() => {
        if (!isLoggedIn) { setUnreadCount(0); return; }
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (!token) return;
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/unread/count`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(r => setUnreadCount(r.data ?? 0)).catch(() => {});
    }, [isLoggedIn, location.pathname]);

    // Total unread chat messages — refetched on navigation and whenever the
    // chat page signals a change via the `chat:unread` window event.
    useEffect(() => {
        if (!isLoggedIn) { setChatUnread(0); return; }
        const fetchChatUnread = () => {
            const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            if (!token) return;
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/chat/unread/count`, {
                headers: { Authorization: `Bearer ${token}` },
            }).then(r => setChatUnread(r.data ?? 0)).catch(() => {});
        };
        fetchChatUnread();
        window.addEventListener('chat:unread', fetchChatUnread);
        return () => window.removeEventListener('chat:unread', fetchChatUnread);
    }, [isLoggedIn, location.pathname]);

    useEffect(() => {
        const handleClickOutside = e => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => { setDropdownOpen(false); }, [location.pathname]);

    const handleLogout = async () => {
        try {
            const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            if (token) {
                await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            logout();
            navigate('/');
        } catch (e) {
            console.error('Logout failed:', e.message);
        }
    };

    /* shared link style for dropdown items */
    const linkStyle = {
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 16px', fontSize: '14px',
        color: 'var(--ink-700)', textDecoration: 'none',
        transition: 'background 0.12s', cursor: 'pointer',
        background: 'none', border: 'none', width: '100%',
        textAlign: 'left', fontFamily: 'var(--font-sans)',
    };
    const hover = e => e.currentTarget.style.background = 'var(--ink-50)';
    const unhover = e => e.currentTarget.style.background = '';

    return (
        <header className="ix-header">
            <div className="ix-container">
                <div style={{
                    height: '72px', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between', gap: '16px',
                }}>

                    {/* Logo */}
                    <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', textDecoration: 'none', flexShrink: 0 }}>
                        <span className="ix-brand-mark"><img src="/brand/interim-mark-white.svg" alt="Interim" /></span>
                        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, gap: '3px' }}>
                            <span style={{ fontWeight: 600, fontSize: '17px', letterSpacing: '-0.018em', color: 'var(--ink-950)' }}>
                                Interim<span style={{ color: 'var(--teal-700)' }}>.</span>
                            </span>
                            <span className="mono" style={{ fontSize: '10.5px', letterSpacing: '0.04em', color: 'var(--ink-500)' }}>
                                Work on demand
                            </span>
                        </span>
                    </Link>

                    {/* Centre nav — desktop only */}
                    <nav className="ix-desktop-nav">
                        <div className="ix-nav-inner">
                            <Link to="/"           className={`ix-nav-item${location.pathname === '/'           ? ' ix-active' : ''}`}>Home</Link>
                            <Link to="/get-job"    className={`ix-nav-item${location.pathname === '/get-job'    ? ' ix-active' : ''}`}>Get Job</Link>
                            {isLoggedIn && <>
                                <Link to="/create-job"  className={`ix-nav-item${location.pathname === '/create-job'  ? ' ix-active' : ''}`}>Create Job</Link>
                                <Link to="/my-listings" className={`ix-nav-item${location.pathname === '/my-listings' ? ' ix-active' : ''}`}>My Listings</Link>
                                <Link to={`/profile/${user?.id}/applications`} className={`ix-nav-item${location.pathname.includes('/applications') ? ' ix-active' : ''}`}>My Applications</Link>
                            </>}
                        </div>
                    </nav>

                    {/* Right side */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>

                        {isLoggedIn && (
                            /* Wallet chip — desktop only */
                            <Link to="/wallet" className="hidden md:inline-flex" style={{
                                alignItems: 'center', gap: '6px',
                                padding: '6px 12px', borderRadius: 'var(--r-pill)',
                                background: 'var(--teal-50)',
                                border: '1px solid color-mix(in srgb, var(--teal-500) 28%, transparent)',
                                fontSize: '13px', fontWeight: 600,
                                color: 'var(--teal-800)', textDecoration: 'none',
                                fontFamily: 'var(--font-mono)',
                            }}>
                                <Wallet size={14} />
                                €{user?.balance != null ? Number(user.balance).toFixed(2) : '0.00'}
                            </Link>
                        )}

                        {isLoggedIn && (
                            /* Messages / chat inbox */
                            <Link to="/messages" className="ix-chat-btn" aria-label="Messages">
                                <MessageSquare size={17} />
                                {chatUnread > 0 && (
                                    <span className="ix-chat-dot">{chatUnread > 9 ? '9+' : chatUnread}</span>
                                )}
                            </Link>
                        )}

                        {isLoggedIn && (
                            /* Bell / notifications */
                            <Link to="/notifications" className="ix-bell-btn" aria-label="Notifications">
                                <Bell size={17} />
                                {unreadCount > 0 && <span className="ix-bell-dot" aria-hidden="true" />}
                            </Link>
                        )}

                        {isLoggedIn && (
                            /* Post job — desktop only */
                            <Link to="/create-job" className="ix-btn ix-btn-outline ix-btn-sm hidden md:inline-flex">
                                <PlusSquare size={15} /> Post job
                            </Link>
                        )}

                        {/* Single dropdown trigger */}
                        <div ref={dropdownRef} style={{ position: 'relative' }}>
                            <button
                                onClick={() => setDropdownOpen(o => !o)}
                                className="ix-profile-btn"
                                aria-expanded={dropdownOpen}
                                aria-haspopup="true"
                            >
                                <span className="ix-avatar">
                                    {isLoggedIn ? (user?.firstName?.[0] ?? <User size={14} />) : <User size={14} />}
                                </span>
                                <span className="ix-label-desktop" style={{ fontSize: '14px', fontWeight: 500, color: 'var(--ink-900)' }}>
                                    {isLoggedIn ? (user?.firstName || 'Profile') : 'Menu'}
                                </span>
                                <ChevronDown size={14} style={{
                                    color: 'var(--ink-500)',
                                    transform: dropdownOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s',
                                }} />
                            </button>

                            {dropdownOpen && (
                                <div style={{
                                    position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                                    width: '240px', background: 'var(--white)',
                                    border: '1px solid var(--ink-200)', borderRadius: 'var(--r-lg)',
                                    boxShadow: 'var(--shadow-lg)', overflow: 'hidden', zIndex: 200,
                                }}>

                                    {isLoggedIn ? (
                                        <>
                                            {/* Mobile-only nav links */}
                                            <div className="ix-mobile-only" style={{ borderBottom: '1px solid var(--ink-100)', paddingBottom: '4px', marginBottom: '4px' }}>
                                                {[
                                                    { label: 'Home',            to: '/' },
                                                    { label: 'Get Job',         to: '/get-job' },
                                                    { label: 'Post a Job',      to: '/create-job' },
                                                    { label: 'My Listings',     to: '/my-listings' },
                                                    { label: 'My Applications', to: `/profile/${user?.id}/applications` },
                                                ].map(({ label, to }) => (
                                                    <Link key={to} to={to} style={linkStyle}
                                                        onMouseEnter={hover} onMouseLeave={unhover}>
                                                        {label}
                                                    </Link>
                                                ))}
                                            </div>

                                            {[
                                                { label: 'View Profile',  to: `/profile/${user?.id}` },
                                                { label: 'Personal Info', to: `/profile/${user?.id}/personal-info` },
                                                { label: 'Saved Jobs',    to: '/saved-jobs' },
                                                { label: 'Wallet',        to: '/wallet' },
                                            ].map(({ label, to }) => (
                                                <Link key={to} to={to} style={linkStyle}
                                                    onMouseEnter={hover} onMouseLeave={unhover}>
                                                    {label}
                                                </Link>
                                            ))}

                                            <div style={{ borderTop: '1px solid var(--ink-200)', margin: '4px 0' }} />

                                            <button onClick={handleLogout} style={{ ...linkStyle, color: '#b91c1c' }}
                                                onMouseEnter={hover} onMouseLeave={unhover}>
                                                <LogOut size={15} /> Log out
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {/* Mobile-only nav links */}
                                            <div className="ix-mobile-only" style={{ borderBottom: '1px solid var(--ink-100)', paddingBottom: '4px', marginBottom: '4px' }}>
                                                {[
                                                    { label: 'Home',    to: '/' },
                                                    { label: 'Get Job', to: '/get-job' },
                                                ].map(({ label, to }) => (
                                                    <Link key={to} to={to} style={linkStyle}
                                                        onMouseEnter={hover} onMouseLeave={unhover}>
                                                        {label}
                                                    </Link>
                                                ))}
                                            </div>

                                            <Link to="/login" style={linkStyle}
                                                onMouseEnter={hover} onMouseLeave={unhover}>
                                                <LogIn size={15} /> Log in
                                            </Link>
                                            <Link to="/register" style={{ ...linkStyle, color: 'var(--teal-700)', fontWeight: 500 }}
                                                onMouseEnter={hover} onMouseLeave={unhover}>
                                                Sign up free
                                            </Link>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
